<script>
import { useJackpotStore } from '../stores/jackpot/jackpotStore';
import { onMounted, onUnmounted, watch, computed } from 'vue'; 
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n'


export default {
  name: 'JackpotsView',
  
  setup() {
    const { t } = useI18n();
    const route = useRoute();
    const jackpotStore = useJackpotStore();
    const { jackpots, isLoading } = storeToRefs(jackpotStore);
    let updateIntervalId = null;
   

    // Computed para ordenar los jackpots
    const sortedJackpots = computed(() => {
      return [...jackpots.value].sort((a, b) => b.level - a.level);
    });

    // Computed para separar jackpots principales (4-6) de los pequeños (1-2)
    const mainJackpots = computed(() => {
      return sortedJackpots.value.filter(jackpot => jackpot.level >= 3);
    });
    
    const smallJackpots = computed(() => {
      return sortedJackpots.value.filter(jackpot => jackpot.level <= 2);
    });

    // Función para actualizar jackpots
    const updateJackpots = async () => {
      if (route.params.adminId) {
        console.log('🎯 JackpotsView: Actualizando jackpots periódicamente...');
        await jackpotStore.loadExternalJackpots(route.params.adminId);
      }
    };

    onMounted(async () => {
      console.log('JackpotsView montado con params:', route.params);
      if (route.params.adminId) {
        // Carga inicial
        await jackpotStore.loadExternalJackpots(route.params.adminId);
        
        // Configurar actualización periódica
        updateIntervalId = setInterval(() => {
          updateJackpots();
        }, 3000);
      }
    });

    onUnmounted(() => {
      // Limpiar intervalo al desmontar
      if (updateIntervalId) {
        clearInterval(updateIntervalId);
      }
    });

    watch(() => route.params, async (newParams) => {
      if (newParams.adminId) {
        await jackpotStore.loadExternalJackpots(newParams.adminId);
      }
    });

    return {
      jackpots,
      isLoading,
      route,
      sortedJackpots,
      mainJackpots,
      smallJackpots,
      t
    };
  }
}
</script>
<template>
  <div class="jackpots-container">
    <!-- Video de fondo -->
    <video autoplay muted loop id="background-video" class="background-video">
        <source src="/videos/jackpot-bg.mp4" type="video/mp4">
  <source src="/videos/jackpot-bg.webm" type="video/webm">

    </video>
    
    <div class="jackpots-wrapper">
      <div class="sign">
        <span class="fast-flicker">J</span>
        A<span class="flicker">C</span>K
        <span class="flicker">P</span>O
        <span class="fast-flicker">T</span>
      </div>
      
      <div v-if="isLoading" class="loading">{{ t('jackpots.title') }}</div>
      <div v-else-if="jackpots.length === 0" class="no-jackpots">{{ t('jackpots.not_jackpots_found') }}</div>
      <div v-else class="jackpots-container-inner">
        <!-- Jackpots principales (Silver, Gold, Platinum, Diamond) -->
        <div class="main-jackpots">
          <div v-for="jackpot in mainJackpots" 
               :key="jackpot.id" 
               :class="['jackpot-item', `level-${jackpot.level}`]">
            <div class="jackpot-content">
              <span :class="['jackpot-name', `name-glow-${jackpot.level}`]">
                {{ jackpot.name }}
                <!-- Indicador visual de Mystery Mode -->
              <span v-if="jackpot.is_mystery_mode" class="mystery-indicator">
                 💰 CALIENTE 🔥
              </span>
              </span>
              <span class="jackpot-amount">
                ${{ Math.floor(jackpot.current_balance) }}.
                <span class="cents">{{ ((jackpot.current_balance % 1) * 100).toFixed(0).padStart(2, '0') }}</span>
              </span>
            </div>
          </div>
        </div>
        
        <!-- Jackpots pequeños (Iron y Bronze) en línea -->
        <div class="small-jackpots">
          <div v-for="jackpot in smallJackpots" 
               :key="jackpot.id" 
               :class="['jackpot-item', `level-${jackpot.level}`]">
            <div class="jackpot-content">
              <span :class="['jackpot-name', `name-glow-${jackpot.level}`]">
                {{ jackpot.name }}
                <span v-if="jackpot.is_mystery_mode" class="mystery-indicator">
                  💰 CALIENTE 🔥
                </span>
              </span>
              <span class="jackpot-amount">
                ${{ Math.floor(jackpot.current_balance) }}.
                <span class="cents">{{ ((jackpot.current_balance % 1) * 100).toFixed(0).padStart(2, '0') }}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
/* Video de fondo */
.background-video {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
}

/* Alternativamente, si usas un GIF de fondo */
.jackpots-container {
  min-height: 100vh;
  position: relative;
  overflow: visible;
}

.jackpots-wrapper {
  position: relative;
  z-index: 1;
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: flex-end; /* Alinea todo a la derecha */
  padding: 1rem 2rem; /* Añade padding derecho */
}

/* Título JACKPOT - mantenerlo igual pero centrado */
.sign {
  position: absolute;
  top: 3%;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  letter-spacing: 2px;
  font-size: 4em;
  color: #ffe6ff;
  text-shadow: 0 0 0.6rem #ffe6ff, 
               0 0 1.5rem #ff65bd,
               -0.2rem 0.1rem 1rem #ff65bd, 
               0.2rem 0.1rem 1rem #ff65bd,
               0 -0.5rem 2rem #ff2483, 
               0 0.5rem 3rem #ff2483;
  animation: shine 2s forwards, flicker 3s infinite;
}

.flicker {
  animation: shine 2s forwards, blink 3s 2s infinite;
}

.fast-flicker {
  animation: shine 2s forwards, blink 10s 1s infinite;
}

@keyframes shine {
  0% {
    color: #6b1839;
    text-shadow: none;
  }
  100% {
    color: #ffe6ff;
    text-shadow: 0 0 0.6rem #ffe6ff, 
                 0 0 1.5rem #ff65bd,
                 -0.2rem 0.1rem 1rem #ff65bd, 
                 0.2rem 0.1rem 1rem #ff65bd,
                 0 -0.5rem 2rem #ff2483, 
                 0 0.5rem 3rem #ff2483;
  }
}

@keyframes blink {
  0%, 22%, 36%, 75% {
    color: #ffe6ff;
    text-shadow: 0 0 0.6rem #ffe6ff, 
                 0 0 1.5rem #ff65bd,
                 -0.2rem 0.1rem 1rem #ff65bd, 
                 0.2rem 0.1rem 1rem #ff65bd,
                 0 -0.5rem 2rem #ff2483, 
                 0 0.5rem 3rem #ff2483;
  }
  28%, 33% {
    color: #ff65bd;
    text-shadow: none;
  }
  82%, 97% {
    color: #ff2483;
    text-shadow: none;
  }
}

/* Contenedor de jackpots */
.jackpots-container-inner {
  margin-top: 10%;
  display: flex;
  flex-direction: column;
  align-items: flex-end; /* Alinea a la derecha */
  width: 100%;
}

.main-jackpots {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1.2rem;
  margin-bottom: 1.5rem;
}

/* Jackpots pequeños en línea */
.small-jackpots {
  display: flex;
  gap: 1rem;
  justify-content: flex-end; /* Alinea a la derecha */
  flex-wrap: wrap;
}

.jackpot-item {
  position: relative;
  border-radius: 50px;
  overflow: hidden;
  padding: 2px;
}

.jackpot-content {
  background: rgba(0, 0, 0, 0.8); /* Añade transparencia para que se vea el video de fondo */
  border-radius: 50px;
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* Colores y efectos por nivel */
.level-6 {
  background: #ff0000;
  box-shadow: 0 0 10px #ff4545;
}

.level-5 {
  background: #ff00ff;
  box-shadow: 0 0 10px #ff00ff;
}

.level-4 {
  background: #ffd700;
  box-shadow: 0 0 10px #ffd700;
}

.level-3 {
  background: #00ffff;
  box-shadow: 0 0 10px #00ffff;
}

.level-2 {
  background: #ff6b6b;
  box-shadow: 0 0 10px #ff6b6b;
}

.level-1 {
  background: #00ff00;
  box-shadow: 0 0 15px #00ff00;
}

/* Nombres de jackpot */
.level-6 .jackpot-name { color: #ff0000; animation: neonGlowJackpot 4s ease-in-out infinite; }
.level-5 .jackpot-name { color: #ff00ff; animation: neonGlowJackpot 4s ease-in-out infinite;}
.level-4 .jackpot-name { color: #ffd700; animation: neonGlowJackpot 4s ease-in-out infinite;}
.level-3 .jackpot-name { color: #00ffff; animation: neonGlowJackpot 4s ease-in-out infinite;}
.level-2 .jackpot-name { color: #ff6b6b; animation: neonGlowJackpot 4s ease-in-out infinite;}
.level-1 .jackpot-name { color: #00ff00; animation: neonGlowJackpot 4s ease-in-out infinite;}

.jackpot-name {
  font-size: 2.5rem;
  font-weight: bold;
  text-transform: uppercase;
}

@keyframes neonGlowJackpot {
  0% {
    color: #ff0000;
   
  }
  25% {
    color: #fffb00;
   
  }
  50% {
    color: #ff00ff;
   
  }
  75% {
    color: #20ff14;
    
  }
  100% {
    color: #08e6f6;
    
  }
}

.jackpot-amount {
  font-size: 2rem;
  font-weight: bold;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  color: #fff;
  animation: metalicGlow 1.5s infinite alternate;
}

@keyframes metalicGlow {
    0% {
        color: #ffd700;
        text-shadow: 0 0 5px #ffd700, 0 0 8px #ffd700;
    }
    50% {
        color: #39fd03;
    }
    100% {
        color: #ff00ea;
    }
}

/* Tamaños específicos - ajustados para el nuevo diseño */
.level-6 .jackpot-content { border:#ff0000 solid 5px; width:1020px; }
.level-6 .jackpot-name { font-size: 2.2rem; }
.level-6 .jackpot-amount { font-size: 3rem; }

.level-5 .jackpot-content {border:#ec02e8 solid 5px; width: 950px; }
.level-5 .jackpot-name { font-size: 2.1rem; }
.level-5 .jackpot-amount { font-size: 2.8rem; }

.level-4 .jackpot-content { border:#e3ff0b solid 5px; width: 850px; }
.level-4 .jackpot-name { font-size: 2rem; }
.level-4 .jackpot-amount { font-size: 2.6rem; }

.level-3 .jackpot-content { border:#00d0ff solid 5px; width: 750px; }
.level-3 .jackpot-name { font-size: 1.9rem; }
.level-3 .jackpot-amount { font-size: 2.4rem; }

/* Jackpots pequeños - tamaño reducido para poder ponerlos inline */
.level-2 .jackpot-content { border:#fd0a0a solid 5px; width: 330px; }
.level-2 .jackpot-name { font-size: 1.4rem; }
.level-2 .jackpot-amount { font-size: 1.8rem; }

.level-1 .jackpot-content { border:#45ff02 solid 5px; width: 330px; }
.level-1 .jackpot-name { font-size: 1.4rem; }
.level-1 .jackpot-amount {font-size: 1.8rem;}

/* Responsive adjustments */
@media (max-width: 768px) {
  .sign {
    font-size: 3em;
  }
  
  .level-6 .jackpot-content { width: 100%; max-width: 600px; }
  .level-5 .jackpot-content { width: 100%; max-width: 580px; }
  .level-4 .jackpot-content { width: 100%; max-width: 550px; }
  .level-3 .jackpot-content { width: 100%; max-width: 520px; }
  .level-2 .jackpot-content { width: 100%; max-width: 280px; }
  .level-1 .jackpot-content { width: 100%; max-width: 280px; }
  
  .small-jackpots {
    flex-direction: column;
    align-items: flex-end;
  }
}
</style>